import { UserStatus } from "../flatbuffers/generated/archive/user-status";

/**
 * A user's status as the generated `UserStatus` enum, from whichever shape the
 * value arrived in.
 *
 * Both shapes are real: a FlatBuffer-decoded user carries the numeric enum
 * value, while offline and legacy JSON paths carry the name as a string. Code
 * that compares `user.status === UserStatus.ACTIVE` without normalising is
 * therefore correct for one path and silently false for the other.
 *
 * Note what this does NOT do: it does not decide whether a status is present.
 * An absent value normalises to `NEW`, which is a real status a user can hold,
 * so callers wanting to render "no status" must test the raw field — and must
 * test it for null rather than for truthiness, because `UserStatus.NEW` is 0.
 *
 * Previously copy-pasted, identically, into `DrawerUserInformation.tsx` and
 * `StepUsersRoles.tsx`.
 */
export function toUserStatus(value: unknown): UserStatus {
  if (typeof value === "number" && UserStatus[value] !== undefined) {
    return value as UserStatus;
  }

  const normalized = String(value ?? "").toUpperCase();
  switch (normalized) {
    case "ACTIVE":
      return UserStatus.ACTIVE;
    case "NEW":
      return UserStatus.NEW;
    case "BLOCKED":
    case "INACTIVE":
      return UserStatus.BLOCKED;
    case "OFFLINE_CREATED":
      return UserStatus.OFFLINE_CREATED;
    case "OFFLINE_SYNCED":
      return UserStatus.OFFLINE_SYNCED;
    case "OFFLINE_ACTIVE":
      return UserStatus.OFFLINE_ACTIVE;
    case "DELETED":
      return UserStatus.DELETED;
    default:
      return UserStatus.NEW;
  }
}

/**
 * Whether a user record carries a status at all.
 *
 * `!user.status` is the wrong test and was the bug this replaces: the numeric
 * form of `UserStatus.NEW` is 0, so every invited-but-not-yet-activated account
 * — precisely the ones an administrator opens a drawer to look at — read as
 * "no status" and rendered a dash.
 */
export const hasUserStatus = (value: unknown): boolean => value != null && value !== "";
